/**
 * Signup Vuex module — wizard state + PIN flow state for the self-serve
 * free-tier signup flow (feature flag: VUE_APP_FREETIER_SIGNUP_ENABLED).
 *
 * State is persisted to sessionStorage so a mid-wizard refresh does not
 * lose progress. sessionStorage (NOT localStorage) is intentional — this
 * is not a real auth session yet, and sessionStorage auto-clears on tab
 * close.
 *
 * Actions call into the Track A3 API helpers in `cacheService.ts`. Mock
 * mode (`VUE_APP_SIGNUP_MOCK=true`) is honored end-to-end so the wizard
 * can be driven with synthetic responses before the backend is live.
 *
 * Phase D Track D2 — state shape migration:
 *  - `wizardData.company` now carries `cadence` ('light'|'balanced'|
 *    'intense', default 'balanced') and `delivery_time` (HH:mm 24h,
 *    default '08:00'). The legacy `location` field is removed.
 *  - `wizardData.people` replaces the nested `wizardData.teams`
 *    structure with a flat list of people. The first entry (index 0) is
 *    the signer themselves, marked with `you: true`, and is seeded onto
 *    the array as soon as `verifyPin` succeeds.
 *  - Submission payload is rebuilt around the flat people array.
 *
 * Phase D Track D4 — cleanup:
 *  - The `teams` getter and `SET_TEAMS` mutation back-compat shims have
 *    been removed. All callers (WizardReviewStep, LaunchEditModal) now
 *    derive their per-team views directly from `wizardData.people`.
 */

import firebase from 'firebase/app'
import 'firebase/auth'
import router from '@/router'
import {
  requestSignupPin,
  verifySignupPin,
  registerFreetrial,
  getCompany,
  getByUser,
  buildSessionUser,
  setSessionUserData,
  RegisterFreetrialPayload,
} from '../core/cacheService'

const SESSION_KEY = 'signup_state'
const SIGNUP_MOCK = process.env.VUE_APP_SIGNUP_MOCK === 'true'

const DEFAULT_CORE_VALUES = [
  { id: 'respect', name: 'Respect' },
  { id: 'integrity', name: 'Integrity' },
  { id: 'teamwork', name: 'Teamwork' },
  { id: 'excellence', name: 'Excellence' },
  { id: 'care', name: 'Care' },
]

const COUNTRY_NAME_TO_CODE: { [name: string]: string } = {
  Thailand: 'TH',
  'United States': 'US',
  'United Kingdom': 'GB',
  Singapore: 'SG',
  Philippines: 'PH',
  India: 'IN',
  Australia: 'AU',
  Indonesia: 'ID',
  Vietnam: 'VN',
  Malaysia: 'MY',
  Japan: 'JP',
  'South Korea': 'KR',
  China: 'CN',
  'Hong Kong': 'HK',
  Taiwan: 'TW',
  Germany: 'DE',
  France: 'FR',
  Spain: 'ES',
  Italy: 'IT',
  Netherlands: 'NL',
  Sweden: 'SE',
  Norway: 'NO',
  Denmark: 'DK',
  Finland: 'FI',
  Switzerland: 'CH',
  Ireland: 'IE',
  Portugal: 'PT',
  Belgium: 'BE',
  Austria: 'AT',
  Poland: 'PL',
  Canada: 'CA',
  Mexico: 'MX',
  Brazil: 'BR',
  Argentina: 'AR',
  Chile: 'CL',
  Colombia: 'CO',
  'United Arab Emirates': 'AE',
  'Saudi Arabia': 'SA',
  Qatar: 'QA',
  Israel: 'IL',
  Turkey: 'TR',
  Egypt: 'EG',
  'South Africa': 'ZA',
  Kenya: 'KE',
  Nigeria: 'NG',
  'New Zealand': 'NZ',
}

function toCountryCode(country: string): string {
  const value = String(country || '').trim()
  if (!value) return ''
  if (/^[A-Za-z]{2}$/.test(value)) return value.toUpperCase()
  return COUNTRY_NAME_TO_CODE[value] || value
}

function buildDefaultState(): any {
  return {
    pinRequested: false,
    pinVerified: false,
    email: '',
    // Captured on the entry page alongside email + company name so the
    // signer's row in Step 3's people table is pre-filled and the
    // backend payload's admin.firstname/lastname can be derived.
    signerName: '',
    signupToken: null,
    wizardData: {
      company: {
        name: '',
        country: '',
        timezone: null,
        cadence: 'balanced',
        delivery_time: '08:00',
      },
      coreValues: DEFAULT_CORE_VALUES.map((v) => ({ ...v })),
      people: [],
      selectedRewardIds: [],
    },
    submitting: false,
    submitError: null,
  }
}

function loadFromSession(): any {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    const raw = window.sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Merge with defaults so new fields added later don't break hydration.
    const base = buildDefaultState()
    const incomingWiz = (parsed && parsed.wizardData) || {}
    return {
      ...base,
      ...parsed,
      wizardData: {
        ...base.wizardData,
        ...incomingWiz,
        company: {
          ...base.wizardData.company,
          ...(incomingWiz.company || {}),
        },
        people: Array.isArray(incomingWiz.people) ? incomingWiz.people.slice() : [],
      },
    }
  } catch (e) {
    // Corrupt JSON or inaccessible sessionStorage — fall back to defaults.
    return null
  }
}

function writeToSession(state: any) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
  } catch (e) {
    // Quota exceeded or inaccessible — silently ignore.
  }
}

function clearSession() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return
    window.sessionStorage.removeItem(SESSION_KEY)
  } catch (e) {
    // Ignore.
  }
}

/**
 * Vuex plugin that persists the `signup` namespaced state slice to
 * sessionStorage on every mutation that targets it. Wire into the store
 * `plugins` array.
 */
export const signupPersistencePlugin = (store: any) => {
  store.subscribe((mutation: any, rootState: any) => {
    if (!mutation.type || !mutation.type.startsWith('signup/')) return
    if (mutation.type === 'signup/RESET') {
      clearSession()
      return
    }
    if (rootState && rootState.signup) {
      writeToSession(rootState.signup)
    }
  })
}

const hydrated = loadFromSession()

/**
 * Build the /freetrial payload from a signup state slice. Pure function
 * so the same shape can be inspected (test-mode overlay) and submitted
 * (submitSignup action) without drift.
 */
function splitSignerName(full: string): { firstname: string; lastname: string } {
  const trimmed = String(full || '').trim()
  if (!trimmed) return { firstname: '', lastname: '' }
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return { firstname: parts[0], lastname: '' }
  return { firstname: parts[0], lastname: parts.slice(1).join(' ') }
}

export function buildSubmitPayload(state: any): RegisterFreetrialPayload {
  const wiz = state && state.wizardData ? state.wizardData : {}
  const company = wiz.company || {}
  const coreValuesList: Array<{ id?: string; name: string }> =
    Array.isArray(wiz.coreValues) ? wiz.coreValues : []
  const peopleList: any[] = Array.isArray(wiz.people) ? wiz.people : []
  const selectedRewardIds: string[] = Array.isArray(wiz.selectedRewardIds)
    ? wiz.selectedRewardIds
    : []

  const hashtag: { [id: string]: { name: string; name_th: string } } = {}
  coreValuesList.forEach((cv, idx) => {
    const id =
      (cv && cv.id) ||
      (cv && cv.name
        ? cv.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
        : `value-${idx}`)
    hashtag[id] = {
      name: cv.name || '',
      name_th: cv.name || '',
    }
  })

  return {
    company: {
      name: company.name || '',
      country: toCountryCode(company.country),
      timezone: typeof company.timezone === 'number' ? company.timezone : 0,
      cadence: (company.cadence as 'light' | 'balanced' | 'intense') || 'balanced',
      delivery_time: company.delivery_time || '08:00',
      plan_tier: 'free',
      brand_color: '#6C30CC',
      brand_logo_url: null,
      hashtag,
      onboarding_completed: true,
    },
    core_values: coreValuesList.map((cv) => ({ label: cv.name || '' })),
    people: peopleList.map((p: any) => ({
      name: p.name || '',
      email: p.email || '',
      team: p.team || '',
      managerEmail: p.managerEmail || null,
      you: !!p.you,
    })),
    selected_reward_ids: selectedRewardIds,
    admin: {
      email: (state && state.email) || '',
      ...splitSignerName(state && state.signerName),
    },
  }
}

export default {
  namespaced: true,
  state: hydrated || buildDefaultState(),
  mutations: {
    SET_PIN_REQUESTED(state: any, value: boolean) {
      state.pinRequested = !!value
    },
    SET_PIN_VERIFIED(state: any, value: boolean) {
      state.pinVerified = !!value
    },
    SET_EMAIL(state: any, value: string) {
      state.email = value || ''
    },
    SET_SIGNER_NAME(state: any, value: string) {
      state.signerName = value || ''
    },
    SET_SIGNUP_TOKEN(state: any, value: string | null) {
      state.signupToken = value || null
    },
    UPDATE_COMPANY(state: any, patch: any) {
      // Shallow merge so callers can patch a subset of fields (e.g.
      // cadence-only) without clobbering the rest.
      state.wizardData.company = {
        ...state.wizardData.company,
        ...(patch || {}),
      }
    },
    SET_CORE_VALUES(state: any, values: any[]) {
      state.wizardData.coreValues = Array.isArray(values) ? values.slice() : []
    },
    SET_PEOPLE(state: any, people: any[]) {
      state.wizardData.people = Array.isArray(people) ? people.slice() : []
    },
    SET_SELECTED_REWARDS(state: any, ids: string[]) {
      state.wizardData.selectedRewardIds = Array.isArray(ids) ? ids.slice() : []
    },
    SET_SUBMITTING(state: any, value: boolean) {
      state.submitting = !!value
    },
    SET_SUBMIT_ERROR(state: any, value: string | null) {
      state.submitError = value || null
    },
    RESET(state: any) {
      const fresh = buildDefaultState()
      state.pinRequested = fresh.pinRequested
      state.pinVerified = fresh.pinVerified
      state.email = fresh.email
      state.signerName = fresh.signerName
      state.signupToken = fresh.signupToken
      state.wizardData = fresh.wizardData
      state.submitting = fresh.submitting
      state.submitError = fresh.submitError
    },
  },
  actions: {
    /**
     * Email entry → request PIN. Commits the email on success so the
     * verify step has it without the component re-passing it.
     */
    async requestPin(
      { commit }: any,
      { email, companyName, signerName }:
        { email: string; companyName?: string; signerName?: string }
    ) {
      if (!email) throw new Error('Email is required')
      await requestSignupPin({ email, companyName })
      commit('SET_EMAIL', email)
      if (signerName !== undefined) commit('SET_SIGNER_NAME', signerName)
      commit('SET_PIN_REQUESTED', true)
    },

    /**
     * PIN entry → exchange for signup JWT. Uses the email committed by
     * requestPin. Throws on 401 so the component can show an inline
     * error and let the user retry.
     *
     * On success we ALSO seed `wizardData.people[0]` with the signer so
     * Step 4 can pin them at the top of the people table without each
     * step having to re-derive who "you" is.
     */
    async verifyPin({ commit, state }: any, { pin }: { pin: string }) {
      if (!state.email) throw new Error('Email not set — restart signup')
      if (!pin) throw new Error('PIN is required')
      const { signupToken } = await verifySignupPin({ email: state.email, pin })
      commit('SET_SIGNUP_TOKEN', signupToken)
      commit('SET_PIN_VERIFIED', true)

      // Seed the signer row at index 0 if not already present. This is
      // idempotent on resume because we keep the signer's existing
      // entry untouched if one already exists.
      const existing = Array.isArray(state.wizardData.people)
        ? state.wizardData.people
        : []
      const hasSigner = existing.some((p: any) => p && p.you)
      if (!hasSigner) {
        const next = [
          {
            name: state.signerName || '',
            email: state.email,
            team: '',
            managerEmail: null,
            you: true,
          },
          ...existing,
        ]
        commit('SET_PEOPLE', next)
      }
    },

    /**
     * Final submit — builds the extended /freetrial payload from
     * wizardData, calls the backend, signs the user in with the returned
     * custom token, hydrates the normal authen session state, and
     * navigates to the authenticated home. Mock mode skips the Firebase
     * sign-in so the UI can be exercised without real credentials.
     */
    async submitSignup({ commit, state, dispatch }: any) {
      commit('SET_SUBMITTING', true)
      commit('SET_SUBMIT_ERROR', null)

      try {
        if (!state.signupToken) {
          throw new Error('Signup token missing — verify PIN first')
        }

        const payload = buildSubmitPayload(state)

        const result:any= await registerFreetrial({
          payload,
          signupToken: state.signupToken,
        })

        // In mock mode the customToken is fake; signInWithCustomToken
        // would reject. Skip the Firebase hydration entirely — callers
        // (Phase B wizard review step) will still see a clean success
        // and the signup state gets cleared below.
        if (result.data) {
          await firebase.auth().signInWithCustomToken(result.data.customToken)

          // Hydrate the authen session the same way loginpincode does so
          // the rest of the app (header, permissions, routing) sees a
          // fully-formed user.
          const realUser = await getByUser(state.email)
          if (realUser) {
            const companyData = await getCompany(realUser.company)
            const sessionUser = buildSessionUser(realUser, companyData, false)
            setSessionUserData(sessionUser)
            commit('authen/setUserLogin', sessionUser, { root: true })
            commit('authen/setCompanyData', companyData, { root: true })
          }
        }

        commit('SET_SUBMITTING', false)
        commit('RESET')

        // Navigate to the authenticated home. In mock mode the app will
        // bounce back to /login because there's no real session — that's
        // expected and documented in the plan's exit criteria.
        if (router && typeof router.replace === 'function') {
          router.replace('/').catch(() => { /* ignore navigation duplicates */ })
        }

        return result
      } catch (rawErr) {
        const err: any = rawErr
        const message =
          (err && err.message) ||
          (err && err.response && err.response.data && err.response.data.message) ||
          'Signup failed'
        commit('SET_SUBMITTING', false)
        commit('SET_SUBMIT_ERROR', message)
        throw err
      }
    },

    reset({ commit }: any) {
      commit('RESET')
    },
    async verifyEmail({ commit }: any, { email }: { email: string }) {
      const emailKey = email.replace(/\./g, '||')
      const result = await firebase.database().ref(`/users/${emailKey}`).once('value')
      if (result.exists()) {
        throw new Error('Email already exists')
      }
      return true
    },

    /**
     * Test-mode shortcut: skip the email + PIN flow and drop the user
     * straight into the wizard with a synthetic state. Only callable
     * when VUE_APP_SIGNUP_MOCK is on. Useful for testing step UX in
     * isolation without going through the entry/verify forms each
     * time. The signer row is seeded with a placeholder email so
     * Step 4's people table works.
     */
    testQuickStart({ commit }: any, payload?: { email?: string; signerName?: string }) {
      if (!SIGNUP_MOCK) {
        // Refuse outside mock mode so we never accidentally bypass the
        // real auth flow in production.
        throw new Error('testQuickStart is only available in mock mode')
      }
      const testEmail = (payload && payload.email) || 'test@happily.ai'
      const testName = (payload && payload.signerName) || 'Test User'
      commit('SET_EMAIL', testEmail)
      commit('SET_SIGNER_NAME', testName)
      commit('SET_PIN_REQUESTED', true)
      commit('SET_SIGNUP_TOKEN', 'test-mode-signup-token-' + Date.now())
      commit('SET_PIN_VERIFIED', true)
      commit('SET_PEOPLE', [
        {
          name: testName,
          email: testEmail,
          team: '',
          managerEmail: null,
          you: true,
        },
      ])
      if (router && typeof router.replace === 'function') {
        router.replace('/signup/wizard/company').catch(() => { /* ignore */ })
      }
    },
  },
  getters: {
    email: (state: any) => state.email,
    signerName: (state: any) => state.signerName,
    signupToken: (state: any) => state.signupToken,
    wizardData: (state: any) => state.wizardData,
    coreValues: (state: any) => state.wizardData.coreValues,
    people: (state: any) => state.wizardData.people,
    selectedRewardIds: (state: any) => state.wizardData.selectedRewardIds,
    isPinVerified: (state: any) =>
      !!state.pinVerified && !!state.signupToken,
    submitting: (state: any) => state.submitting,
    submitError: (state: any) => state.submitError,
    /**
     * The /freetrial payload that submitSignup would send right now,
     * built from current wizardData. Used by the test-mode inspector
     * and the test-submit dialog so the live state and the would-be
     * payload always match.
     */
    payloadPreview: (state: any) => buildSubmitPayload(state),
    isMockMode: () => SIGNUP_MOCK,
  },
}
